import React, { useMemo } from 'react';
import moment from 'moment';
import { formatFileSize } from 'utils/helpers';
import { GreenDotIcon } from 'components/common/icons/GreenDotIcon';
import { EMLIcon } from 'components/common/icons/fileIcons/EMLIcon';
import { JPGIcon } from 'components/common/icons/fileIcons/JPGIcon';
import { Note as NoteType, TypeOfEntityType } from 'models/ComunicationHub';
import { useNote } from './useNote';
import styles from './../styles.module.scss';
import { Avatar, Button, Col, CommunicationHubEntityType, OverflowList, Row, Typography } from 'components';
import { DeleteIcon, EditIcon, JPEGIcon, MP3Icon, PDFIcon, PinIcon, PNGIcon } from 'components/common/icons';

interface NoteItemProps {
  note: NoteType;
  entity: CommunicationHubEntityType;
  isFinalStatus?: boolean;
  hasAccessToWrite: boolean;
  hasAccessToOtherNoteDelete: boolean;
  hasAccessToOtherNoteWrite: boolean;
  onExpand: (entities: Record<string, any[]>) => void;
  onAttachmentsExpand: (attachments: Record<string, any>[]) => void;
  onEditNote: (note: NoteType) => void;
  onDeleteNote: (id?: string) => void;
  noteRef?: (node: Element | null) => void | undefined;
}

export const FILE_ICON_MAPPING: Record<string, React.ReactNode> = {
  pdf: <PDFIcon />,
  eml: <EMLIcon />,
  jpg: <JPGIcon />,
  png: <PNGIcon />,
  jpeg: <JPEGIcon />,
  mp3: <MP3Icon />,
};

export function getFileIcon(extension: string): React.ReactNode {
  const ext = extension?.toLowerCase();
  return FILE_ICON_MAPPING[ext] || null;
}

export const Note: React.FC<NoteItemProps> = ({
  note,
  entity,
  isFinalStatus,
  hasAccessToWrite,
  hasAccessToOtherNoteDelete,
  hasAccessToOtherNoteWrite,
  onExpand,
  onAttachmentsExpand,
  onEditNote,
  onDeleteNote,
  noteRef,
}) => {
  const { onPinChange, onRouteChange, userId, relations } = useNote({ note, entity });
  const canEdit = useMemo(
    () => !isFinalStatus && (hasAccessToOtherNoteWrite || userId === note.userId),
    [isFinalStatus, hasAccessToOtherNoteWrite, userId, note.userId]
  );
  const canDelete = useMemo(
    () => !isFinalStatus && (hasAccessToOtherNoteDelete || userId === note.userId),
    [isFinalStatus, hasAccessToOtherNoteDelete, userId, note.userId]
  );

  return (
    <Row gutter={[40, 0]} ref={noteRef}>
      <Col style={{ paddingTop: 6 }}>
        <Row gutter={8}>
          <Col>
            <GreenDotIcon />
          </Col>
          <Col>
            <Typography.Text className={'font-size-12'} type={'secondary'}>
              {moment(note.createDate).format('hh:mmA')}
            </Typography.Text>
          </Col>
        </Row>
      </Col>
      <Col span={20}>
        <Row gutter={[10, 0]} wrap={false}>
          <Col>
            <Avatar src={note?.user?.files?.AVATAR} size="large" className={styles.avatar}>
              {note.user?.firstName?.charAt(0)}
              {note.user?.lastName?.charAt(0)}
            </Avatar>
          </Col>
          <Col span={24}>
            <Row gutter={[0, 6]}>
              <Col span={24}>
                <Row gutter={[0, 2]}>
                  <Col span={24}>
                    <Row justify={'space-between'}>
                      <Col>
                        <Typography.Text className={'font-weight-600'}>
                          {`${note.user?.firstName} ${note.user?.lastName}`}
                        </Typography.Text>
                      </Col>
                      <Col>
                        <Row gutter={[4, 0]}>
                          <Col>
                            {hasAccessToWrite && !isFinalStatus && (
                              <Button
                                size={'small'}
                                style={{ height: '20px', width: '20px' }}
                                type={'text'}
                                icon={<PinIcon pinned={note.pinned} />}
                                onClick={() => onPinChange(note.id)}
                              />
                            )}
                          </Col>
                          <Col>
                            {canEdit && (
                              <Button
                                size={'small'}
                                style={{ height: '20px', width: '20px' }}
                                type={'text'}
                                icon={<EditIcon />}
                                onClick={() => onEditNote(note)}
                              />
                            )}
                          </Col>
                          <Col>
                            {canDelete && (
                              <Button
                                size={'small'}
                                style={{ height: '20px', width: '20px' }}
                                type={'text'}
                                icon={<DeleteIcon />}
                                onClick={() => onDeleteNote(note.id)}
                              />
                            )}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={24}>
                    <Typography.Text type={'secondary'}>{note.note}</Typography.Text>
                  </Col>
                </Row>
              </Col>
              <Col span={24} style={{ width: '100%' }}>
                <Row wrap={false} align={'middle'}>
                  <OverflowList
                    items={relations}
                    itemRenderer={(label) => {
                      return (
                        <Button
                          key={label.id}
                          className={styles.relation_entity}
                          type={'text'}
                          size={'small'}
                          variant={'text'}
                          onClick={() => onRouteChange(label.entity as TypeOfEntityType, label.id)}
                        >
                          {label.label}:{label.name || label.uuid}
                        </Button>
                      );
                    }}
                    overflowRenderer={(hiddenCount: number) => (
                      <Button
                        size={'small'}
                        type={'text'}
                        variant={'text'}
                        className={styles.relation_entity}
                        style={{ color: '#05796B' }}
                        onClick={() => onExpand(note.relations)}
                      >
                        +{hiddenCount}
                      </Button>
                    )}
                  />
                </Row>
              </Col>
              <Col span={24}>
                <Row gutter={10} align={'middle'} wrap={false}>
                  <OverflowList
                    items={note.attachments}
                    itemRenderer={(attachment) => {
                      return (
                        <Col key={attachment.id}>
                          <Row gutter={[12, 0]} align={'middle'} style={{ maxWidth: '256px' }} wrap={false}>
                            <Col>
                              <Button
                                type="link"
                                icon={getFileIcon(attachment.extension)}
                                download
                                title={attachment.fileName}
                                rel="noopener noreferrer"
                                href={attachment.files?.ATTACHMENTS}
                              />
                            </Col>
                            <Col style={{ width: 'min-content' }}>
                              <Row>
                                <Col span={24}>
                                  <Typography.Text
                                    ellipsis={{ tooltip: true }}
                                    style={{ maxWidth: '200px' }}
                                    className={'font-size-12 font-weight-500'}
                                  >
                                    {attachment.fileName}
                                  </Typography.Text>
                                </Col>
                                <Col>
                                  <Typography.Text className={'font-size-12'}>
                                    {formatFileSize(attachment.size || 0)}
                                  </Typography.Text>
                                </Col>
                              </Row>
                            </Col>
                          </Row>
                        </Col>
                      );
                    }}
                    overflowRenderer={(hiddenCount: number) => (
                      <Button
                        size={'small'}
                        type={'text'}
                        variant={'text'}
                        className={styles.relation_entity}
                        style={{ color: '#05796B' }}
                        onClick={() => onAttachmentsExpand(note.attachments)}
                      >
                        +{hiddenCount}
                      </Button>
                    )}
                  />
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
